import React, { useState, useEffect } from "react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import {
  usePagination,
  useSortBy,
  useRowSelect,
  useTable,
  useGlobalFilter,
} from "react-table";
import { makeStyles } from "@material-ui/core/styles";
import Box from "@material-ui/core/Box";
import Grid from "@material-ui/core/Grid";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TableSortLabel from "@material-ui/core/TableSortLabel";
import Paper from "@material-ui/core/Paper";
import TextField from "@material-ui/core/TextField";
import DragHandleIcon from "@material-ui/icons/DragHandle";
import MenuItem from "@material-ui/core/MenuItem";
import Button from "@material-ui/core/Button";
import { IconButton } from "@material-ui/core";
import { Delete, Edit } from "@material-ui/icons";
import { CustomCheckbox } from "../../../util";
import { Link, useRouteMatch } from "react-router-dom";
import ActionBtnsWithOrdering from "../ActionBtnsWithOrdering";
import SearchField from "../SearchField";
import PaginationBar from "../../common/PaginationBar";

const useStylesAll = makeStyles((theme) => ({
  iconBtn: {
    color: "#CCCCCC",
    margin: 5,
  },
  pagination: {
    "& .MuiTextField-root": {
      width: "35px",
      minWidth: "auto",
    },
    "& .MuiSelect-root": {
      paddingTop: "3px",
      paddingBottom: "3px",
    },
    "& input": {
      padding: "4px 0",
      marginLeft: "10px",
      marginRight: "10px",
    },
    "& button": {
      background: "white",
      color: "#cccccc",
      border: "1px solid #CCCCCC",
      minWidth: "auto",
      width: "24px",
      height: "24px",
      margin: "4px 0",
      "&.left": {
        borderRadius: "4px 0 0 4px",
        marginLeft: "10px",
      },
      "&.right": {
        borderRadius: " 0 4px 4px 0",
      },
    },
  },
  alignRight: {
    justifyContent: "flex-end",
  },
  searchField: {
    "& .MuiBox-root-22": {
      width: "30%",
    }
  }
}));

const IsDraggingContext = React.createContext(false);

const reorder = (list, startIndex, endIndex) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);

  return result;
};

class DraggableTableCell extends React.Component {
  getSnapshotBeforeUpdate(prevProps) {
    if (!this.ref) {
      return null;
    }

    const isDragStarting =
      this.props.isDragOccurring && !prevProps.isDragOccurring;

    if (!isDragStarting) {
      return null;
    }

    const { width, height } = this.ref.getBoundingClientRect();

    const snapshot = {
      width,
      height,
    };

    return snapshot;
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    const ref = this.ref;
    if (!ref) {
      return;
    }

    if (snapshot) {
      if (ref.style.width === snapshot.width) {
        return;
      }
      ref.style.width = `${snapshot.width}px`;
      ref.style.height = `${snapshot.height}px`;
      return;
    }

    if (this.props.isDragOccurring) {
      return;
    }

    // inline styles not applied
    if (ref.style.width == null) {
      return;
    }

    // no snapshot and drag is finished - clear the inline styles
    ref.style.removeProperty("height");
    ref.style.removeProperty("width");
  }

  setRef = (ref) => {
    this.ref = ref;
  };

  render() {
    return (
      <TableCell
        style={{ paddingTop: 6, paddingBottom: 6 }}
        ref={this.setRef}
        align={this.props.align}
      >
        {this.props.children}
      </TableCell>
    );
  }
}

const DraggableTableRow = ({
  snapshot,
  data,
  row,
  provided,
  className,
  orderingDisabled,
}) => {
  return (
    <IsDraggingContext.Consumer>
      {(isDragging) => (
        <TableRow
          className={className}
          ref={provided.innerRef}
          isDragging={snapshot.isDragging}
          {...provided.draggableProps}
        >
          {row.cells.map((cell) => {
            if (cell.column.id !== "dragHandle") {
              return (
                <DraggableTableCell isDragOccurring={isDragging} scope="row">
                  {cell.render("Cell")}
                </DraggableTableCell>
              );
            } else {
              return null;
            }
          })}
          <TableCell
            style={{ paddingTop: 6, paddingBottom: 6, color: "#CCCCCC" }}
            {...(!orderingDisabled && { ...provided.dragHandleProps })}
          >
            <DragHandleIcon />
          </TableCell>
        </TableRow>
      )}
    </IsDraggingContext.Consumer>
  );
};

const IndeterminateCheckbox = React.forwardRef(
  ({ indeterminate, ...rest }, ref) => {
    const defaultRef = React.useRef();
    const resolvedRef = ref || defaultRef;

    useEffect(() => {
      resolvedRef.current.indeterminate = indeterminate;
    }, [resolvedRef, indeterminate]);

    return <CustomCheckbox type="checkbox" ref={resolvedRef} {...rest} />;
  }
);

export default function DragAndDropTable(props) {
  const {
    globalFilter,
    orderingDisabled,
    handleSorting,
    handleSearch,
    searchString,
    setSearchString,
    fetchData,
    data,
    setData,
    columns,
    bulkDeleteApi,
    subject,
    saveOrderingApi,
    disabled,
    /**
     * optional below
     */
    editDeleteAction = false,
    handleDelete = () => {
      return;
    },
    deleteByRow = false,
    withPagination = false,
    setParentPageIndex = () => {
      return;
    },
    setParentPageSize = () => {
      return;
    },
    parentPageIndex,
    totalElements,
    totalPages,
    addBtnLink = null,
    addBtnName = null,
    isUnpaged = false,
    editPath = null,
  } = props;
  const match = useRouteMatch();
  const CustomLink = React.useMemo(
    () =>
      React.forwardRef((linkProps, ref) => <Link ref={ref} {...linkProps} />),
    []
  );
  const [isDragging, setIsDragging] = useState(false);
  const {
    getTableProps,
    headerGroups,
    getTableBodyProps,
    state: { pageIndex, pageSize },
    rows,
    prepareRow,
    setPageSize,
    gotoPage,
    previousPage,
    nextPage,
    canPreviousPage,
    canNextPage,
    setGlobalFilter,
  } = useTable(
    {
      columns,
      data,
      initialState: {
        pageIndex: parentPageIndex,
        pageSize: withPagination ? 10 : 99999999,
      },
      manualPagination: true,
      pageCount: totalPages,
    },
    useGlobalFilter,
    useSortBy,
    usePagination,
    useRowSelect,
    (hooks) => {
      editDeleteAction
        ? hooks.visibleColumns.push((columns) => [
            // Let's make a column for selection
            {
              id: "selection",
              // The header can use the table's getToggleAllRowsSelectedProps method
              // to render a checkbox
              Header: ({ getToggleAllRowsSelectedProps }) => (
                <div>
                  <IndeterminateCheckbox
                    {...{ ...getToggleAllRowsSelectedProps(), disabled }}
                  />
                </div>
              ),
              // The cell can use the individual row's getToggleRowSelectedProps method
              // to the render a checkbox
              Cell: ({ row }) => (
                <div>
                  <IndeterminateCheckbox
                    {...{ ...row.getToggleRowSelectedProps(), disabled }}
                  />
                </div>
              ),
            },

            ...columns,
            {
              id: "action",
              // The header can use the table's getToggleAllRowsSelectedProps method
              // to render a checkbox
              Header: () => <div>Action</div>,
              // The cell can use the individual row's getToggleRowSelectedProps method
              // to the render a checkbox
              Cell: ({ row }) => (
                <div>
                  <IconButton
                    className={classes.iconBtn}
                    size="small"
                    component={CustomLink}
                    to={`${editPath || match.url}/${row.original.id}`}
                  >
                    <Edit fontSize="small" />
                  </IconButton>
                  {disabled || (
                    <IconButton
                      className={classes.iconBtn}
                      size="small"
                      // component={CustomLink}
                      onClick={() => {
                        handleDelete(deleteByRow ? row.original : row.original.id);
                      }}
                    >
                      <Delete fontSize="inherit" />
                    </IconButton>
                  )}
                </div>
              ),
            },
            // The drag handle can be used to drag the table's rows individually
            {
              id: "dragHandle",
              Header: () => <></>,
            },
          ])
        : hooks.visibleColumns.push((columns) => [
            // Let's make a column for selection
            {
              id: "selection",
              // The header can use the table's getToggleAllRowsSelectedProps method
              // to render a checkbox
              Header: ({ getToggleAllRowsSelectedProps }) => (
                <div>
                  <IndeterminateCheckbox
                    {...{ ...getToggleAllRowsSelectedProps(), disabled }}
                  />
                </div>
              ),
              // The cell can use the individual row's getToggleRowSelectedProps method
              // to the render a checkbox
              Cell: ({ row }) => (
                <div>
                  <IndeterminateCheckbox
                    {...{ ...row.getToggleRowSelectedProps(), disabled }}
                  />
                </div>
              ),
            },

            ...columns,
            // The drag handle can be used to drag the table's rows individually
            {
              id: "dragHandle",
              Header: () => <></>,
            },
          ]);
    }
  );

  useEffect(() => {
    setGlobalFilter(globalFilter);
    setParentPageIndex(0)
  }, [globalFilter]);

  useEffect(() => {
    setParentPageSize(pageSize);
  }, [pageSize]);

  useEffect(() => {
    setParentPageIndex(pageIndex);
  }, [pageIndex]);

  const onBeforeDragStart = () => {
    setIsDragging(true);
  };

  //callback passed to dragdrop context
  const onDragEnd = (result) => {
    // dropped outside the list
    if (!result.destination) {
      return;
    }

    const items = reorder(data, result.source.index, result.destination.index);

    setData(items);
  };

  const classes = useStylesAll();

  return (
    <>
      <Grid container spacing={2} justify="space-between">
        <Grid container item xs={12} justify="space-between">
          <Grid container item xs={6}>
            <ActionBtnsWithOrdering
              saveOrdering={saveOrderingApi}
              orderingDisabled={orderingDisabled}
              rows={rows}
              fetchData={fetchData}
              bulkDeleteApi={bulkDeleteApi}
              subject={subject}
              disabled={disabled}
            />
          </Grid>
          <Grid container item xs={6} className={classes.alignRight}>
            {addBtnLink != null &&
              <Button
              variant="contained"
              color="primary"
              component={Link}
              to={addBtnLink}
              disabled={disabled}
            >
              {addBtnName}
            </Button>
            }
            <SearchField
              onSearch={handleSearch}
              searchString={searchString}
              setSearchString={setSearchString}
              subject={subject}
              isFullWidth={addBtnLink == null}
            />
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <Box width="100%">
            <IsDraggingContext.Provider value={isDragging}>
              <Paper>
                <Table
                  aria-label="simple table"
                  component="ul"
                  {...getTableProps()}
                >
                  <TableHead component="div">
                    {headerGroups.map((headerGroup) => {
                      handleSorting(false);
                      return (
                        <TableRow {...headerGroup.getHeaderGroupProps()}>
                          {headerGroup.headers.map((column) => {
                            if (column.isSorted) {
                              handleSorting(true);
                            }
                            return (
                              <TableCell
                                {...column.getHeaderProps(
                                  column.getSortByToggleProps()
                                )}
                                style={{
                                  paddingTop: 6,
                                  paddingBottom: 6,
                                  verticalAlign: "baseline",
                                }}
                              >
                                {column.render("Header")}
                                {column.id !== "selection" &&
                                column.id !== "action" &&
                                column.id !== "dragHandle" ? (
                                  <TableSortLabel
                                    active={column.isSorted}
                                    // react-table has an unsorted state which is not treated here
                                    direction={
                                      column.isSortedDesc ? "desc" : "asc"
                                    }
                                  />
                                ) : null}
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      );
                    })}
                  </TableHead>
                  <DragDropContext
                    onBeforeDragStart={onBeforeDragStart}
                    onDragEnd={onDragEnd}
                  >
                    <Droppable droppableId="droppable" type="QUESTIONS">
                      {(provided, snapshot) => (
                        <TableBody
                          component="div"
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                          {...getTableBodyProps()}
                        >
                          {rows.map((row, index) => {
                            prepareRow(row);
                            return (
                              <Draggable
                                key={row.id}
                                draggableId={row.id}
                                index={index}
                                isDragDisabled={disabled}
                              >
                                {(provided, snapshot) => (
                                  <DraggableTableRow
                                    key={row.id}
                                    data={row.original}
                                    row={row}
                                    provided={provided}
                                    snapshot={snapshot}
                                    orderingDisabled={props.orderingDisabled}
                                    match={match}
                                  />
                                )}
                              </Draggable>
                            );
                          })}
                          {provided.placeholder}
                        </TableBody>
                      )}
                    </Droppable>
                  </DragDropContext>
                </Table>
              </Paper>
              <Grid container spacing={3} justify="space-between">
                <Grid container item xs={7}>
                  <ActionBtnsWithOrdering
                    saveOrdering={saveOrderingApi}
                    orderingDisabled={orderingDisabled}
                    rows={rows}
                    fetchData={fetchData}
                    bulkDeleteApi={bulkDeleteApi}
                    subject={subject}
                    disabled={disabled}
                  />
                </Grid>
                {withPagination && (
                  <Grid container item xs={5} justifyContent="flex-end" alignItems="center">
                    <PaginationBar
                      pageSize={pageSize}
                      setPageSize={setPageSize}
                      gotoPage={gotoPage}
                      previousPage={previousPage}
                      nextPage={nextPage}
                      canPreviousPage={canPreviousPage}
                      canNextPage={canNextPage}
                      pageIndex={pageIndex}
                      totalCount={totalElements}
                    />
                  </Grid>
                )}
              </Grid>
            </IsDraggingContext.Provider>
          </Box>
        </Grid>
      </Grid>
    </>
  );
}
